import * as React from "react"
import {Box, Chip, Grid, Tooltip, Typography} from "@mui/material";
import {AppLink} from "@ui-seeker/app";
import {CheckboxSeeker, IconSeeker} from "@ui-seeker/core";
import {MatchersLazyImage} from "../../@components";

import {BACKEND_APIS} from "@constants";
import {useProxiedImage} from "../../@hooks";
import {rawMatcherImageComparatorModalData} from "../@signals";


// Función para determinar el color del badge según el score
const getScoreColor = (distance) => {
	const score = parseFloat(distance)
	if (isNaN(score)) return 'default'

	if (score >= 0.9) return 'success'      // Verde - Excelente match
	if (score >= 0.7) return 'primary'      // Azul - Buen match
	if (score >= 0.5) return 'warning'      // Naranja - Match medio
	return 'error'                          // Rojo - Match bajo
}

// Memoised on purpose: the selection is a signal the whole Content reads, so every change
// (a click on a card, an arrow in the comparison modal) re-rendered the complete list — 644
// cards for a brand like Dior, seconds of main-thread work before the modal or the border
// moved. With `isSelected` as a boolean prop only the two cards that change re-render. The
// Grid item wrapper lives inside for the same reason: a styled MUI Grid per row costs an
// emotion serialisation on every parent render, memo or not.
function SkuOption({ productID, productName, productThumbnail, siteID, skuID, isSelected=false, data, setSelectedSKU, brandOptionsIndex=0, isIAOption=false }) {
	const isAlreadyMapped = data.alreadyMapped === true
	const border = isSelected ? "4px solid" : '1px solid'
	const borderColor = isAlreadyMapped ? "warning.main" : "primary.light"

	const handleCheck = () => setSelectedSKU(isSelected ? null : skuID)
	// SKU image from SKU master table
	const skuImageURL = `${BACKEND_APIS.BACKEND}/sku/image/${skuID}`
	// Product thumbnail for comparison modal
	const { decodedURL: __productThumbnail, imageResourceURL: productImageURL } = useProxiedImage(productThumbnail)
	// A sku with no url on this site used to open an empty tab, so it is left as plain text
	const hasSiteURL = data.hasSiteURL !== false
	const skuNameLabel = <Typography display="flex" alignItems="center" gap={0.5} fontWeight="bold" fontSize="0.85rem" lineHeight={1.2}>
		{data.skuName}
		{hasSiteURL && <IconSeeker icon="external-link" sx={{ color: 'text.disabled', fontSize: "0.7rem" }} />}
	</Typography>

	return <Grid item xs={12}><Grid container border={border} borderColor={borderColor} borderRadius={1} p={0.5} cursor="pointer" spacing={1} onClick={handleCheck} alignItems="center">
		{ isAlreadyMapped && <Grid item xs={12}>
			<Box display="flex" alignItems="center" gap={0.5} bgcolor="warning.light" borderRadius={1} px={1} py={0.5}>
				<IconSeeker icon="triangle-exclamation" sx={{ color: 'warning.dark', fontSize: "0.9rem" }} />
				<Typography fontWeight="bold" fontSize="0.75rem" color="warning.dark">
					Already Mapped: {data.idProductMapped}
				</Typography>
			</Box>
		</Grid> }

		<Grid item xs="auto" display="flex" alignItems="center">
			<CheckboxSeeker label="" checked={isSelected} onChange={handleCheck} sx={{ mx: 0, p: 0 }} />
			<Box position="relative">
				{/* Lazy on purpose: a brand can list 600+ skus and each image is a redirect through the
				    back plus a full-size png from the bucket, so eager loading queued hundreds of
				    requests at once and the list sat on spinners. Only the cards in view fetch. */}
				<MatchersLazyImage
					useSpinner
					src={skuImageURL}
					sx={{ border: '1px solid', borderColor: 'divider', width: 70, height: 70, objectFit: 'contain', cursor: 'pointer', borderRadius: 1 }}
					onClick={() => rawMatcherImageComparatorModalData.value = {
						title: data.skuName,
						fullUrlLeft: productImageURL,
						fullUrlRight: skuImageURL,
						productName: productName,
						candidateName: data.skuName,
						targetIndex: brandOptionsIndex
					}}
				/>
				{isIAOption && (
					<Box position="absolute" top={-6} right={-6}>
						<Chip
							icon={<IconSeeker icon="sparkles" sx={{ fontSize: "0.6rem !important", color: "inherit !important" }} />}
							label={data.AIDistance ? `${Math.round(parseFloat(data.AIDistance) * 100)}%` : "N/A"}
							color={getScoreColor(data.AIDistance)}
							variant="filled"
							size="small"
							sx={{ fontSize: "0.65rem", height: 18, fontWeight: 'bold' }}
						/>
					</Box>
				)}
			</Box>
		</Grid>

		<Grid item xs display="flex" flexDirection="column" justifyContent="center" pl={1}>
			{hasSiteURL
				? <AppLink
					to={`${BACKEND_APIS.MATCHERS}/skus/main-url/${skuID}/${siteID}`}
					target="_blank"
					onClick={event => event.stopPropagation()}
				>
					{skuNameLabel}
				</AppLink>
				: <Tooltip title="No URL for this retailer">{skuNameLabel}</Tooltip>
			}
			<Box display="flex" flexWrap="wrap" gap={1} mt={0.5}>
				<Typography fontSize="0.75rem" color="text.secondary">ID: {skuID}</Typography>
				{data.ean && <Typography fontSize="0.75rem" color="text.secondary">EAN: {data.ean}</Typography>}
				{data.reference && <Typography fontSize="0.75rem" color="text.secondary">Ref: {data.reference}</Typography>}
				{data.size && <Typography fontSize="0.75rem" color="text.secondary">Size: {data.size}</Typography>}
				{data.color && <Typography fontSize="0.75rem" color="text.secondary">Color: {data.color}</Typography>}
			</Box>
		</Grid>
	</Grid></Grid>
}

export default React.memo(SkuOption)
